import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import DownloadAppModal from '@components/modal/DownloadAppModal';
import UnauthorizedUserModal from '@components/modal/UnauthorizedUserModal';
import ShareModal, { ShareData } from '@components/modal/ShareModal';

const MAX_MODALS = 1;

type ModalType = 'downloadApp' | 'unauthorized' | 'share';

type ModalProps = {
  id: string;
  type: ModalType;
  onConfirm?: () => void;
  shareData?: ShareData;
};

type ModalContextType = {
  showDownloadAppModal: (onConfirm?: () => void) => void;
  showUnauthorizedModal: (onConfirm?: () => void) => void;
  showShareModal: (shareData: ShareData) => void;
  hideModal: (id: string) => void;
};

type ModalContextProvider = {
  children: ReactNode;
};

export const ModalContext = createContext<ModalContextType>({
  showDownloadAppModal: () => {},
  showUnauthorizedModal: () => {},
  showShareModal: () => {},
  hideModal: () => {},
});

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    console.log('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider: FC<ModalContextProvider> = ({ children }) => {
  const [modals, setModals] = useState<ModalProps[]>([]);

  const hideModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const showDownloadAppModal = useCallback((onConfirm?: () => void) => {
    const id = `modal-${Date.now().toString()}`;
    const modal: ModalProps = {
      id,
      type: 'downloadApp',
      onConfirm,
    };

    setModals((_prev) => {
      const newModals = [modal];
      return newModals.slice(0, MAX_MODALS);
    });

    return id;
  }, []);

  const showUnauthorizedModal = useCallback((onConfirm?: () => void) => {
    const id = `modal-${Date.now().toString()}`;
    const modal: ModalProps = {
      id,
      type: 'unauthorized',
      onConfirm,
    };

    setModals((_prev) => {
      const newModals = [modal];
      return newModals.slice(0, MAX_MODALS);
    });

    return id;
  }, []);

  const showShareModal = useCallback((shareData: ShareData) => {
    const id = `modal-${Date.now().toString()}`;
    const modal: ModalProps = {
      id,
      type: 'share',
      shareData,
    };

    setModals((_prev) => {
      const newModals = [modal];
      return newModals.slice(0, MAX_MODALS);
    });

    return id;
  }, []);

  return (
    <ModalContext.Provider
      value={{
        showDownloadAppModal,
        showUnauthorizedModal,
        showShareModal,
        hideModal,
      }}
    >
      {children}
      {modals.map((modal) => {
        switch (modal.type) {
          case 'downloadApp':
            return (
              <DownloadAppModal
                key={modal.id}
                isShown={true}
                onConfirm={modal.onConfirm || (() => {})}
                onClose={() => hideModal(modal.id)}
              />
            );
          case 'unauthorized':
            return (
              <UnauthorizedUserModal
                key={modal.id}
                isShown={true}
                onClose={() => hideModal(modal.id)}
              />
            );
          case 'share':
            return modal.shareData ? (
              <ShareModal
                key={modal.id}
                isShown={true}
                onClose={() => hideModal(modal.id)}
                shareData={modal.shareData}
              />
            ) : null;
          default:
            return null;
        }
      })}
    </ModalContext.Provider>
  );
};
